import { Select as SelectPrimitive } from "@kobalte/core/select";
import { Check, ChevronsUpDown, Plus } from "@fleet/icons";
import { type JSX, Show, splitProps } from "solid-js";
import { cn } from "@time-tracker/shared";

interface SelectOption {
  value: string;
  label: JSX.Element;
  /** Plain-text value used for typeahead / ARIA. Falls back to `value`. */
  textValue?: string;
  disabled?: boolean;
}

/**
 * CTA rendered inside the dropdown when the Select has no options (AWS-style:
 * an empty combobox offers "Create X" / "Go to X" instead of a dead control).
 *
 * Pass `onSelect` to run an action (open a creation dialog) or `href` to render
 * a link (navigate to the screen that provides the options). Provide exactly one.
 * `can` (default `true`) gates the CTA on permission — when `false` the empty
 * dropdown falls back to a plain "no options" message and no creation affordance
 * is shown. Omit `emptyAction` entirely when the source is not creatable.
 */
export interface SelectEmptyAction {
  label: string;
  onSelect?: () => void;
  href?: string;
  /** Permission gate. When false, the CTA is hidden. Defaults to true. */
  can?: boolean;
  /** Leading icon for the CTA. Defaults to a plus glyph. */
  icon?: JSX.Element;
  /** Optional helper line shown above the CTA (e.g. "No projects yet"). */
  description?: string;
}

interface SelectProps {
  /** Currently selected value (controlled). */
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: JSX.Element;
  disabled?: boolean;
  /** Class applied to the trigger (input-like surface). */
  class?: string;
  /** Class applied to the floating panel. */
  contentClass?: string;
  /** Accessible label for the control. */
  "aria-label"?: string;
  id?: string;
  name?: string;
  /**
   * Shown inside the dropdown when `options` is empty. Lets the user jump to the
   * place that creates options instead of facing a dead Select. See
   * {@link SelectEmptyAction}.
   */
  emptyAction?: SelectEmptyAction;
  /** Message shown in the empty dropdown when there is no (permitted) CTA.*/
  emptyLabel?: string;
}

/**
 * Premium Select built on @kobalte/core — drop-in replacement for native
 * `<select>`. Theme-safe (card/popover/accent tokens), keyboard + typeahead +
 * ARIA out of the box, animated panel, check on the selected item.
 */
export function Select(props: SelectProps) {
  const [local] = splitProps(props, [
    "value",
    "onChange",
    "options",
    "placeholder",
    "disabled",
    "class",
    "contentClass",
    "aria-label",
    "id",
    "name",
    "emptyAction",
    "emptyLabel",
  ]);

  const selectedOption = () => local.options.find((o) => o.value === local.value);
  const isEmpty = () => local.options.length === 0;

  return (
    <SelectPrimitive<SelectOption>
      options={local.options}
      optionValue="value"
      optionTextValue={(o) => o.textValue ?? o.value}
      optionDisabled="disabled"
      value={selectedOption() ?? null}
      // Required single-select: re-clicking the already selected option must NOT
      // deselect it (Kobalte toggle would emit null and clear the field
      // before submit). May be changed to another option, not cleared by re-clicking.
      disallowEmptySelection
      onChange={(option) => {
        if (option) {
          local.onChange(option.value);
        }
      }}
      disabled={local.disabled}
      name={local.name}
      placeholder={local.placeholder}
      gutter={6}
      sameWidth
      itemComponent={(itemProps) => (
        <SelectPrimitive.Item
          item={itemProps.item}
          class={cn(
            "relative flex cursor-pointer select-none items-center justify-between gap-2 rounded-lg",
            "px-3 py-2 text-sm outline-none transition-colors duration-150",
            "text-popover-foreground",
            "data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground",
            "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
          )}
        >
          <SelectPrimitive.ItemLabel class="truncate">
            {itemProps.item.rawValue.label}
          </SelectPrimitive.ItemLabel>
          <SelectPrimitive.ItemIndicator class="shrink-0 text-accent-foreground motion-safe:animate-checkmark-pop">
            <Check class="h-4 w-4 stroke-[3]" />
          </SelectPrimitive.ItemIndicator>
        </SelectPrimitive.Item>
      )}
    >
      <SelectPrimitive.Trigger
        id={local.id}
        aria-label={local["aria-label"]}
        class={cn(
          "group flex w-full items-center justify-between gap-2 rounded-[16px]",
          "border border-border bg-card px-4 py-3 text-sm text-foreground",
          "transition-[border-color,box-shadow,transform] duration-200",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:border-accent/60",
          "data-[expanded]:ring-2 data-[expanded]:ring-ring/40 data-[expanded]:border-accent/60",
          "disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer",
          local.class,
        )}
      >
        <SelectPrimitive.Value<SelectOption> class="truncate text-left">
          {(state) => (
            <Show when={state.selectedOption()} fallback={local.placeholder}>
              {(opt) => <span class="truncate">{opt().label}</span>}
            </Show>
          )}
        </SelectPrimitive.Value>
        <SelectPrimitive.Icon class="shrink-0 text-muted-foreground transition-transform duration-200 group-data-[expanded]:rotate-180 motion-reduce:transition-none">
          <ChevronsUpDown class="h-4 w-4" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          class={cn(
            "ui-select-content z-[300] overflow-hidden rounded-xl border border-border",
            "bg-popover text-popover-foreground shadow-2xl outline-none",
            local.contentClass,
          )}
        >
          <Show
            when={!isEmpty()}
            fallback={<SelectEmptyContent action={local.emptyAction} label={local.emptyLabel} />}
          >
            <SelectPrimitive.Listbox class="max-h-[min(18rem,40vh)] overflow-y-auto p-1.5 space-y-0.5" />
          </Show>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive>
  );
}

/**
 * Content shown inside an empty Select dropdown. Renders the `emptyAction` CTA
 * (creation button or navigation link) when present and permitted, otherwise a
 * muted "no options" message. The CTA closes the dropdown on activation.
 *
 * The CTA is keyboard-reachable (it is a native button/anchor, auto-focusable
 * inside the portalled panel) and inherits the panel's opacity-only entrance, so
 * it respects `prefers-reduced-motion` via the global guard like the rest of the UI.
 */
export function SelectEmptyContent(props: { action?: SelectEmptyAction; label?: string }) {
  const ctaClass = cn(
    "flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm font-medium outline-none",
    "text-popover-foreground transition-colors duration-150 cursor-pointer",
    "hover:bg-accent hover:text-accent-foreground",
    "focus-visible:bg-accent focus-visible:text-accent-foreground focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
  );

  const ctaIcon = () => props.action?.icon ?? <Plus class="h-4 w-4 shrink-0 text-accent" />;

  return (
    <Show
      when={props.action && props.action.can !== false ? props.action : undefined}
      fallback={
        <div class="px-3 py-3 text-center text-sm text-muted-foreground">
          {/* Copy lives at the call site so this primitive stays i18n-clean; the
              neutral em-dash is the last-resort placeholder when no label is given. */}
          {props.label ?? "—"}
        </div>
      }
    >
      {(action) => (
        <div class="space-y-1 p-1.5">
          <Show when={action().description}>
            <p class="px-3 pt-1 text-xs text-muted-foreground">{action().description}</p>
          </Show>
          <Show
            when={action().href}
            fallback={
              <button
                type="button"
                class={ctaClass}
                onClick={() => {
                  action().onSelect?.();
                }}
              >
                {ctaIcon()}
                <span class="truncate">{action().label}</span>
              </button>
            }
          >
            <a href={action().href} class={ctaClass}>
              {ctaIcon()}
              <span class="truncate">{action().label}</span>
            </a>
          </Show>
        </div>
      )}
    </Show>
  );
}
